import { Headquarter } from '@repositories/models';

/**
 * DATA TEMPORAL. La sirve `HeadquartersService` mientras el back no exponga
 * el endpoint; cuando exista, se borra este archivo junto con la bandera
 * `useTemporaryData` del servicio.
 *
 * Definición de la ciudad: qué headquarters existen, dónde se apoya su
 * edificio en el lienzo isométrico (2400x1000) y qué se despliega al abrirlos.
 *
 * Los sitios y la jerarquía son los reales de SharePoint. Un headquarter es
 * un sitio sin padre; sus hijos pueden a su vez tener hijos (Finanzas llega a
 * tres niveles), y el panel lateral profundiza a medida que se navega.
 *
 * La posición y el tamaño de cada edificio son de presentación: imitan el
 * reparto del render de referencia (hilera al fondo, los grandes flanqueando
 * el hub de beliv, hilera al frente). Cuando el back exponga el endpoint
 * basta con mapear la respuesta a `Headquarter[]` y conservar estas medidas.
 *
 * Los iconos son nombres de lucide
 * (`<lucide-icon name="...">`, ver `headquarters.icons.ts`).
 */

/** Raíz de los sitios de SharePoint, para no repetirla en cada entrada */
const SITES = 'https://mariposacbc.sharepoint.com/sites';

export const HEADQUARTERS: Headquarter[] = [
  // --- Hilera del fondo -------------------------------------------------
  {
    id: 1,
    name: 'C-Level',
    icon: 'award',
    color: '#14b8a6',
    url: `${SITES}/1stlinebeliv`,
    x: 1200,
    y: 320,
    width: 82,
    height: 132,
    pin: 48,
  },
  {
    id: 2,
    name: 'Finanzas',
    icon: 'landmark',
    color: '#22c55e',
    url: `${SITES}/BelivFinance`,
    x: 820,
    y: 380,
    width: 100,
    height: 150,
    pin: 54,
    children: [
      {
        id: 3,
        name: 'Business Partner',
        icon: 'briefcase',
        url: `${SITES}/BelivFinance/BP`,
        children: [
          { id: 4, name: 'Planning', icon: 'calendar', url: `${SITES}/BelivFinance/BP/Planning` },
          { id: 5, name: 'Controlling', icon: 'percent', url: `${SITES}/BelivFinance/BP/Controlling` },
          { id: 6, name: 'Transforming', icon: 'git-merge', url: `${SITES}/BelivFinance/BP/Transforming` },
        ],
      },
      {
        id: 7,
        name: 'Latam + Europa',
        icon: 'globe',
        url: `${SITES}/BelivFinance/Latam`,
        children: [
          { id: 8, name: 'Controlling', icon: 'percent', url: `${SITES}/BelivFinance/Contabilidad` },
          { id: 9, name: 'Planning', icon: 'calendar', url: `${SITES}/BelivFinance/Planning` },
          { id: 10, name: 'Tesorería', icon: 'credit-card', url: `${SITES}/BelivFinance/Tesoreria` },
        ],
      },
    ],
  },
  {
    id: 11,
    name: 'Comercial',
    icon: 'trending-up',
    color: '#3b82f6',
    url: `${SITES}/BelivOperaciones`,
    x: 1580,
    y: 380,
    width: 100,
    height: 145,
    pin: 54,
    children: [
      { id: 12, name: 'Latam', icon: 'map-pin', url: `${SITES}/BelivSAMCCAR` },
      { id: 13, name: 'Europa', icon: 'map-pin', url: `${SITES}/beliveur` },
    ],
  },

  // --- Flancos, a los costados del hub ----------------------------------
  {
    id: 14,
    name: 'Gente & Gestión',
    icon: 'users',
    color: '#f59e0b',
    url: `${SITES}/BelivGenteGestion`,
    x: 520,
    y: 640,
    width: 92,
    height: 140,
    pin: 54,
  },
  {
    id: 15,
    name: 'Brands',
    icon: 'tag',
    color: '#d946ef',
    url: `${SITES}/BelivBrands`,
    x: 1780,
    y: 600,
    width: 106,
    height: 155,
    pin: 62,
    featured: true,
    children: [
      { id: 16, name: 'Magic Nature', icon: 'lightbulb', url: `${SITES}/belivmagicnature` },
      { id: 17, name: 'Waters', icon: 'droplet', url: `${SITES}/belivwaters` },
      { id: 18, name: 'CSDs', icon: 'disc', url: `${SITES}/CSDsRev` },
      { id: 19, name: 'Energy & Coffee', icon: 'zap', url: `${SITES}/belivenergycoffee` },
      { id: 20, name: 'Insights', icon: 'chart-column', url: `${SITES}/belivinsights` },
      { id: 21, name: 'Innovation', icon: 'flask-conical', url: `${SITES}/BelivInnovation` },
      { id: 22, name: 'Martech', icon: 'target', url: `${SITES}/digitalmartech` },
    ],
  },

  // --- Hilera del frente ------------------------------------------------
  {
    id: 23,
    name: 'Legal',
    icon: 'scale',
    color: '#a855f7',
    url: `${SITES}/BelivLegal`,
    x: 720,
    y: 830,
    width: 90,
    height: 122,
    pin: 48,
  },
  {
    id: 24,
    name: 'R&D',
    icon: 'flask-conical',
    color: '#ec4899',
    url: `${SITES}/BelivRyD`,
    x: 1010,
    y: 890,
    width: 94,
    height: 128,
    pin: 48,
    children: [
      { id: 25, name: 'Innovaciones', icon: 'zap', url: `${SITES}/BelivRyD/Innovation` },
      { id: 26, name: 'Desarrollo', icon: 'git-branch', url: `${SITES}/BelivRyD/RD` },
      { id: 27, name: 'Regulatorio', icon: 'shield', url: `${SITES}/BelivRyD/Regulatory` },
      { id: 28, name: 'Preoject Office Manager', icon: 'clipboard', url: `${SITES}/BelivRyD/POM` },
    ],
  },
  {
    id: 29,
    name: 'Business Management',
    icon: 'target',
    color: '#06b6d4',
    url: `${SITES}/BusinessManagement`,
    x: 1340,
    y: 900,
    width: 106,
    height: 126,
    pin: 48,
    children: [
      {
        id: 30,
        name: 'Data Analytics',
        icon: 'chart-bar',
        url: `${SITES}/BusinessManagement/Data%20Analytics`,
      },
      {
        id: 31,
        name: 'Management System',
        icon: 'settings',
        url: `${SITES}/BusinessManagement/Management%20System`,
      },
      {
        id: 32,
        name: 'Digital Strategy',
        icon: 'compass',
        url: `${SITES}/BusinessManagement/Digital%20Strategy`,
      },
    ],
  },
  {
    id: 33,
    name: 'Supply',
    icon: 'truck',
    color: '#f97316',
    url: `${SITES}/belivsupply`,
    x: 1650,
    y: 830,
    width: 94,
    height: 132,
    pin: 48,
  },
];
